import { Link } from '../../router/Link';
import { useCopy } from '../../strings/useCopy';
import { Panel, Spinner } from '../../ui';
import type { ResolveRoute } from './model';
import { useResolve } from './useResolve';
import layout from '../../ui/layout.module.css';
import styles from './resolve.module.css';

/**
 * §8's `/@handle`, `/c/NAME`, `/user/NAME` and `/clip/ID` — the rows of the URL scheme that cost a
 * request. Renders props (R13): the decisions are in `model.ts`, the query and the redirect in
 * `useResolve.ts`.
 *
 * The success state is deliberately almost invisible. A resolution that works is a redirect, so the
 * only thing this page owes the reader then is a `role="status"` line for a screen reader that would
 * otherwise be told nothing at all before the destination replaces it.
 */
export function ResolveView({ route }: { readonly route: ResolveRoute }) {
  const state = useResolve(route);
  const copy = useCopy().text;
  const words = copy.resolve;

  if (state.kind === 'ready') {
    return (
      <div className={layout.wide}>
        <p className={styles.status} role="status">
          {words.found}
        </p>
      </div>
    );
  }

  if (state.kind === 'resolving') {
    return (
      <div className={layout.wide}>
        <div className={styles.resolving} aria-busy>
          <Spinner />
          <p className={styles.status} role="status">
            {words.lookingUp(state.subject.label)}
          </p>
          <p className={styles.detail}>{state.subject.detail}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={layout.wide}>
      <Panel className={styles.panel}>
        <h1 className={styles.title}>{state.copy.title}</h1>
        <p className={styles.detail}>{state.copy.detail}</p>
        {state.jumpOuts.length === 0 ? null : (
          <p className={styles.detail}>
            {/* §8 asks for a jump-out rather than a dead end: the thing exists somewhere, even when
                this build cannot open it. A plain anchor, because it leaves the app. */}
            {copy.shell.placeholder.openOnBefore}{' '}
            {state.jumpOuts.map((jumpOut) => (
              <a className={styles.jumpOut} key={jumpOut.url} href={jumpOut.url} rel="noreferrer">
                {jumpOut.host}
              </a>
            ))}
            .
          </p>
        )}
        <Link className={styles.back} href={state.homeHref}>
          {copy.shell.placeholder.backToStart}
        </Link>
      </Panel>
    </div>
  );
}
